import { useCallback, useMemo } from 'react';
import type {
  AdminClientOption,
  DiaryHealthSummary,
  ReminderRepairResult,
} from "../models/diaryMaintenance";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
export class AdminApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(
  path: string,
  accessToken: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...init.headers,
    },
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const problem = await res.json();
      message = problem.detail ?? problem.title ?? message;
    } catch {
      /* non-JSON error body */
    }
    if (res.status === 403) message = 'You need System Administrator rights for this page.';
    throw new AdminApiError(res.status, message);
  }

  return res.json() as Promise<T>;
}

const qs = (clientId: number, horizonDays: number) =>
  new URLSearchParams({
    clientId: String(clientId),
    horizonDays: String(horizonDays),
  }).toString();

/**
 * Admin diary maintenance API.
 * Follows the useMovementsApi pattern: plain async functions, token passed at the call site.
 */
export function useDiaryMaintenanceApi() {
  const getClients = useCallback(
    (accessToken: string) =>
      request<AdminClientOption[]>('/admin/diary/clients', accessToken),
    [],
  );

  const getSummary = useCallback(
    (accessToken: string, clientId: number, horizonDays: number) =>
      request<DiaryHealthSummary>(
        `/admin/diary/summary?${qs(clientId, horizonDays)}`,
        accessToken,
      ),
    [],
  );

  const previewReminderRepair = useCallback(
    (accessToken: string, clientId: number, horizonDays: number) =>
      request<ReminderRepairResult>(
        `/admin/diary/reminders/repair/preview?${qs(clientId, horizonDays)}`,
        accessToken,
      ),
    [],
  );

  const applyReminderRepair = useCallback(
    (accessToken: string, clientId: number, horizonDays: number) =>
      request<ReminderRepairResult>(
        `/admin/diary/reminders/repair?${qs(clientId, horizonDays)}`,
        accessToken,
        { method: 'POST' },
      ),
    [],
  );

  return useMemo(
    () => ({ getClients, getSummary, previewReminderRepair, applyReminderRepair }),
    [getClients, getSummary, previewReminderRepair, applyReminderRepair],
  );
}
